import React, {
  useEffect,
  useState,
} from 'react';

import {
  CheckCircle2,
  CreditCard,
  IndianRupee,
  Loader2,
  LockKeyhole,
  ShieldCheck,
  Store,
  FileText,
  CalendarDays,
  Download,
  X,
} from 'lucide-react';

import './SubscriptionPayment.css';

const API_BASE_URL =
  window.location.origin;

/* ======================================================
   VENDOR TOKEN
====================================================== */

const getVendorToken = () => {
  return (
    localStorage.getItem('token') ||
    sessionStorage.getItem('token') ||
    localStorage.getItem('vendor_token') ||
    sessionStorage.getItem('vendor_token') ||
    ''
  );
};

/* ======================================================
   RAZORPAY SCRIPT
====================================================== */

const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    const existingScript =
      document.querySelector(
        'script[src="https://checkout.razorpay.com/v1/checkout.js"]'
      );

    if (existingScript) {
      resolve(true);
      return;
    }

    const script =
      document.createElement(
        'script'
      );

    script.src =
      'https://checkout.razorpay.com/v1/checkout.js';

    script.async = true;

    script.onload = () => {
      resolve(true);
    };

    script.onerror = () => {
      resolve(false);
    };

    document.body.appendChild(
      script
    );
  });
};

/* ======================================================
   FORMAT DATE
====================================================== */

const formatDisplayDate = (
  value
) => {
  if (!value) {
    return '—';
  }

  const date =
    new Date(
      `${String(value).slice(
        0,
        10
      )}T00:00:00`
    );

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return value;
  }

  return date.toLocaleDateString(
    'en-IN',
    {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }
  );
};

/* ======================================================
   COMPONENT
====================================================== */

const SubscriptionPayment = () => {
  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    plansLoading,
    setPlansLoading,
  ] = useState(true);

  const [
    paying,
    setPaying,
  ] = useState(false);

  const [
    selectingPlanId,
    setSelectingPlanId,
  ] = useState(null);

  const [
    subscription,
    setSubscription,
  ] = useState(null);

  const [
    plans,
    setPlans,
  ] = useState([]);

  const [
    payments,
    setPayments,
  ] = useState([]);

  const [
    agreement,
    setAgreement,
  ] = useState(null);

  const [
    agreementOpen,
    setAgreementOpen,
  ] = useState(false);

  const [
    agreementChecked,
    setAgreementChecked,
  ] = useState(false);

  const [
    acceptingAgreement,
    setAcceptingAgreement,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState('');

  const [
    successMessage,
    setSuccessMessage,
  ] = useState('');

  const [
    paymentSuccess,
    setPaymentSuccess,
  ] = useState(null);

  const [
    downloadingInvoiceId,
    setDownloadingInvoiceId,
  ] = useState(null);

  /* ====================================================
     DOWNLOAD INVOICE
  ==================================================== */

  const handleDownloadInvoice =
    async (
      paymentId,
      receiptNo = ''
    ) => {
      try {
        const numericPaymentId =
          Number(paymentId);

        if (
          !Number.isInteger(
            numericPaymentId
          ) ||
          numericPaymentId <= 0
        ) {
          throw new Error(
            'Invoice payment record was not found.'
          );
        }

        setDownloadingInvoiceId(
          numericPaymentId
        );

        setError('');

        const token =
          getVendorToken();

        if (!token) {
          throw new Error(
            'Vendor login session was not found. Please login again.'
          );
        }

        const response =
          await fetch(
            `${API_BASE_URL}/api/vendor/subscription-billing/invoice/${numericPaymentId}`,
            {
              method: 'GET',

              headers: {
                Authorization:
                  `Bearer ${token}`,
              },
            }
          );

        if (!response.ok) {
          const contentType =
            response.headers.get(
              'content-type'
            ) || '';

          let message =
            'Invoice could not be downloaded.';

          if (
            contentType.includes(
              'application/json'
            )
          ) {
            const data =
              await response
                .json()
                .catch(
                  () => ({})
                );

            message =
              data.error ||
              message;
          }

          throw new Error(
            message
          );
        }

        const blob =
          await response.blob();

        if (
          !blob ||
          blob.size === 0
        ) {
          throw new Error(
            'Downloaded invoice file is empty.'
          );
        }

        const blobUrl =
          window.URL.createObjectURL(
            blob
          );

        const safeReceipt =
          String(
            receiptNo ||
              `VAKRA-${numericPaymentId}`
          )
            .replace(
              /[^a-zA-Z0-9_-]/g,
              '-'
            );

        const link =
          document.createElement(
            'a'
          );

        link.href =
          blobUrl;

        link.download =
          `${safeReceipt}-invoice.pdf`;

        link.style.display =
          'none';

        document.body.appendChild(
          link
        );

        link.click();

        document.body.removeChild(
          link
        );

        window.setTimeout(
          () => {
            window.URL.revokeObjectURL(
              blobUrl
            );
          },
          1500
        );
      } catch (
        invoiceError
      ) {
        console.error(
          'Invoice download error:',
          invoiceError
        );

        setError(
          invoiceError.message ||
            'Invoice could not be downloaded.'
        );
      } finally {
        setDownloadingInvoiceId(
          null
        );
      }
    };

  /* ====================================================
     LOAD BILLING DETAILS
  ==================================================== */

  const loadSubscription =
    async (
      silent = false
    ) => {
      try {
        if (!silent) {
          setLoading(true);
        }

        setError('');

        const token =
          getVendorToken();

        if (!token) {
          throw new Error(
            'Vendor login session was not found. Please login again.'
          );
        }

        const response =
          await fetch(
            `${API_BASE_URL}/api/vendor/subscription-billing`,
            {
              method: 'GET',

              headers: {
                Authorization:
                  `Bearer ${token}`,
              },
            }
          );

        const data =
          await response
            .json()
            .catch(
              () => ({})
            );

        if (!response.ok) {
          throw new Error(
            data.error ||
              'Subscription details could not be loaded.'
          );
        }

        setSubscription(
          data.subscription ||
            null
        );

        setAgreement(
          data.agreement ||
            null
        );

        setPayments(
          Array.isArray(
            data.payments
          )
            ? data.payments
            : []
        );

        if (
          data.agreement
            ?.accepted
        ) {
          setAgreementChecked(
            true
          );
        } else {
          setAgreementChecked(
            false
          );
        }
      } catch (
        loadError
      ) {
        console.error(
          'Subscription load error:',
          loadError
        );

        setError(
          loadError.message ||
            'Subscription details could not be loaded.'
        );

        setSubscription(
          null
        );

        setAgreement(
          null
        );

        setPayments([]);
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    };

  /* ====================================================
     LOAD AVAILABLE PLANS
  ==================================================== */

  const loadPlans =
    async () => {
      try {
        setPlansLoading(
          true
        );

        const token =
          getVendorToken();

        if (!token) {
          throw new Error(
            'Vendor login session was not found.'
          );
        }

        const response =
          await fetch(
            `${API_BASE_URL}/api/vendor/subscription-billing/plans`,
            {
              method:
                'GET',

              headers: {
                Authorization:
                  `Bearer ${token}`,
              },
            }
          );

        const data =
          await response
            .json()
            .catch(
              () => ({})
            );

        if (!response.ok) {
          throw new Error(
            data.error ||
              'Subscription plans could not be loaded.'
          );
        }

        setPlans(
          Array.isArray(
            data.plans
          )
            ? data.plans
            : []
        );
      } catch (
        plansError
      ) {
        console.error(
          'Plans load error:',
          plansError
        );

        setError(
          plansError.message ||
            'Subscription plans could not be loaded.'
        );

        setPlans([]);
      } finally {
        setPlansLoading(
          false
        );
      }
    };

  /* ====================================================
     INITIAL LOAD
  ==================================================== */

  useEffect(() => {
    loadSubscription();
    loadPlans();

    // eslint-disable-next-line
  }, []);

  /* ====================================================
     SELECT PLAN
  ==================================================== */

  const handleSelectPlan =
    async (plan) => {
      try {
        setSelectingPlanId(
          plan.planId
        );

        setError('');
        setSuccessMessage('');
        setPaymentSuccess(
          null
        );

        const token =
          getVendorToken();

        if (!token) {
          throw new Error(
            'Vendor session was not found. Please login again.'
          );
        }

        const response =
          await fetch(
            `${API_BASE_URL}/api/vendor/subscription-billing/select-plan`,
            {
              method:
                'POST',

              headers: {
                'Content-Type':
                  'application/json',

                Authorization:
                  `Bearer ${token}`,
              },

              body:
                JSON.stringify(
                  {
                    planId:
                      plan.planId,
                  }
                ),
            }
          );

        const data =
          await response
            .json()
            .catch(
              () => ({})
            );

        if (!response.ok) {
          throw new Error(
            data.error ||
              'Subscription plan could not be selected.'
          );
        }

        setAgreementChecked(
          false
        );

        setAgreement(
          null
        );

        setSuccessMessage(
          data.reselected
            ? `${plan.planName} selected again. You can continue with agreement and payment.`
            : data.changedSelection
              ? `Your subscription selection has been changed to ${plan.planName}. Please review and accept the agreement before payment.`
              : `${plan.planName} selected successfully. Please review and accept the agreement before payment.`
        );

        await loadSubscription(
          true
        );
      } catch (
        selectError
      ) {
        console.error(
          'Plan selection error:',
          selectError
        );

        setError(
          selectError.message ||
            'Subscription plan could not be selected.'
        );
      } finally {
        setSelectingPlanId(
          null
        );
      }
    };

  /* ====================================================
     OPEN AGREEMENT
  ==================================================== */

  const handleOpenAgreement =
    async () => {
      try {
        setError('');

        if (!subscription) {
          throw new Error(
            'Please select a subscription plan first.'
          );
        }

        const token =
          getVendorToken();

        if (!token) {
          throw new Error(
            'Vendor session was not found.'
          );
        }

        const response =
          await fetch(
            `${API_BASE_URL}/api/vendor/subscription-billing/agreement`,
            {
              method:
                'GET',

              headers: {
                Authorization:
                  `Bearer ${token}`,
              },
            }
          );

        const data =
          await response
            .json()
            .catch(
              () => ({})
            );

        if (!response.ok) {
          throw new Error(
            data.error ||
              'Agreement could not be loaded.'
          );
        }

        setAgreement(
          data.agreement ||
            null
        );

        setAgreementChecked(
          Boolean(
            data.agreement
              ?.accepted
          )
        );

        setAgreementOpen(
          true
        );
      } catch (
        agreementError
      ) {
        console.error(
          'Agreement load error:',
          agreementError
        );

        setError(
          agreementError.message ||
            'Agreement could not be loaded.'
        );
      }
    };

  /* ====================================================
     ACCEPT AGREEMENT
  ==================================================== */

  const handleAcceptAgreement =
    async () => {
      try {
        if (
          !agreementChecked
        ) {
          throw new Error(
            'Please confirm that you have read and agree to the VAKRA Service & Subscription Agreement.'
          );
        }

        setAcceptingAgreement(
          true
        );

        setError('');
        setSuccessMessage('');

        const token =
          getVendorToken();

        if (!token) {
          throw new Error(
            'Vendor session was not found.'
          );
        }

        const response =
          await fetch(
            `${API_BASE_URL}/api/vendor/subscription-billing/agreement/accept`,
            {
              method:
                'POST',

              headers: {
                'Content-Type':
                  'application/json',

                Authorization:
                  `Bearer ${token}`,
              },

              body:
                JSON.stringify(
                  {
                    accepted:
                      true,
                  }
                ),
            }
          );

        const data =
          await response
            .json()
            .catch(
              () => ({})
            );

        if (!response.ok) {
          throw new Error(
            data.error ||
              'Agreement could not be accepted.'
          );
        }

        setAgreement(
          data.agreement ||
            agreement
        );

        setAgreementChecked(
          true
        );

        setAgreementOpen(
          false
        );

        setSuccessMessage(
          'Agreement accepted successfully. You can now complete the subscription payment.'
        );

        await loadSubscription(
          true
        );
      } catch (
        acceptError
      ) {
        console.error(
          'Agreement acceptance error:',
          acceptError
        );

        setError(
          acceptError.message ||
            'Agreement could not be accepted.'
        );
      } finally {
        setAcceptingAgreement(
          false
        );
      }
    };

  /* ====================================================
     PAY NOW
  ==================================================== */

  const handlePayNow =
    async () => {
      try {
        setPaying(true);

        setError('');
        setSuccessMessage('');
        setPaymentSuccess(
          null
        );

        const token =
          getVendorToken();

        if (!token) {
          throw new Error(
            'Vendor session expired. Please login again.'
          );
        }

        if (
          !subscription ||
          !subscription.subscriptionId
        ) {
          throw new Error(
            'Please select a subscription plan first.'
          );
        }

        if (
          subscription.paymentStatus ===
          'paid'
        ) {
          throw new Error(
            'This subscription has already been paid.'
          );
        }

        const agreementAccepted =
          Boolean(
            agreement
              ?.accepted
          ) ||
          agreement
            ?.status ===
            'accepted' ||
          agreement
            ?.status ===
            'active';

        if (
          !agreementAccepted
        ) {
          throw new Error(
            'Please review and accept the VAKRA Service & Subscription Agreement before making payment.'
          );
        }

        const razorpayLoaded =
          await loadRazorpayScript();

        if (
          !razorpayLoaded ||
          !window.Razorpay
        ) {
          throw new Error(
            'Payment service could not be loaded. Please try again.'
          );
        }

        /* ================================================
           CREATE ORDER
        ================================================ */

        const orderResponse =
          await fetch(
            `${API_BASE_URL}/api/vendor/subscription-billing/create-order`,
            {
              method:
                'POST',

              headers: {
                'Content-Type':
                  'application/json',

                Authorization:
                  `Bearer ${token}`,
              },

              body:
                JSON.stringify(
                  {}
                ),
            }
          );

        const orderData =
          await orderResponse
            .json()
            .catch(
              () => ({})
            );

        if (
          !orderResponse.ok
        ) {
          throw new Error(
            orderData.error ||
              'Payment order could not be created.'
          );
        }

        if (
          !orderData.keyId ||
          !orderData.razorpayOrderId ||
          !orderData.amount
        ) {
          throw new Error(
            'Payment gateway response is incomplete.'
          );
        }

        /* ================================================
           RAZORPAY
        ================================================ */

        const options = {
          key:
            orderData.keyId,

          amount:
            orderData.amount,

          currency:
            orderData.currency ||
            'INR',

          name:
            'VAKRA',

          description:
            `${
              subscription.planName ||
              'Subscription'
            } Subscription`,

          order_id:
            orderData.razorpayOrderId,

          prefill: {
            name:
              subscription.ownerName ||
              '',

            email:
              subscription.email ||
              '',

            contact:
              subscription.mobile ||
              '',
          },

          notes: {
            subscription_id:
              String(
                subscription.subscriptionId
              ),

            agreement_id:
              String(
                agreement
                  ?.agreementId ||
                  ''
              ),
          },

          /* ==============================================
             PAYMENT SUCCESS
          ============================================== */

          handler:
            async (
              razorpayResponse
            ) => {
              try {
                const verifyResponse =
                  await fetch(
                    `${API_BASE_URL}/api/vendor/subscription-billing/verify-payment`,
                    {
                      method:
                        'POST',

                      headers: {
                        'Content-Type':
                          'application/json',

                        Authorization:
                          `Bearer ${token}`,
                      },

                      body:
                        JSON.stringify(
                          {
                            razorpay_order_id:
                              razorpayResponse
                                .razorpay_order_id,

                            razorpay_payment_id:
                              razorpayResponse
                                .razorpay_payment_id,

                            razorpay_signature:
                              razorpayResponse
                                .razorpay_signature,
                          }
                        ),
                    }
                  );

                const verifyData =
                  await verifyResponse
                    .json()
                    .catch(
                      () =>
                        ({})
                    );

                if (
                  !verifyResponse.ok
                ) {
                  throw new Error(
                    verifyData.error ||
                      'Payment verification failed.'
                  );
                }

                setPaymentSuccess(
                  {
                    paymentId:
                      verifyData
                        .payment
                        ?.paymentId ||
                      null,

                    razorpayPaymentId:
                      verifyData
                        .payment
                        ?.razorpayPaymentId ||
                      razorpayResponse
                        .razorpay_payment_id,

                    receiptNo:
                      verifyData
                        .payment
                        ?.receiptNo ||
                      '',

                    amount:
                      verifyData
                        .payment
                        ?.amount ||
                      subscription.amount ||
                      0,

                    planName:
                      subscription.planName,

                    businessName:
                      subscription.businessName,
                  }
                );

                setSuccessMessage(
                  ''
                );

                await loadSubscription(
                  true
                );

                await loadPlans();
              } catch (
                verificationError
              ) {
                console.error(
                  'Payment verification error:',
                  verificationError
                );

                setError(
                  verificationError.message ||
                    'Payment verification could not be completed.'
                );
              } finally {
                setPaying(
                  false
                );
              }
            },

          modal: {
            ondismiss:
              () => {
                setPaying(
                  false
                );
              },
          },

          theme: {
            color:
              '#2563eb',
          },
        };

        const razorpay =
          new window.Razorpay(
            options
          );

        razorpay.on(
          'payment.failed',
          (response) => {
            setPaying(
              false
            );

            setError(
              response
                ?.error
                ?.description ||
                'Payment failed. Please try again.'
            );
          }
        );

        razorpay.open();
      } catch (
        paymentError
      ) {
        console.error(
          'Payment start error:',
          paymentError
        );

        setError(
          paymentError.message ||
            'Payment could not be started.'
        );

        setPaying(false);
      }
    };

  /* ======================================================
     LOADING
  ====================================================== */

  if (
    loading &&
    plansLoading
  ) {
    return (
      <div className="vsp-page">
        <div className="vsp-loading">
          <Loader2
            size={34}
            className="vsp-spinner"
          />

          <strong>
            Loading Subscription
          </strong>

          <span>
            Loading your billing details and available plans.
          </span>
        </div>
      </div>
    );
  }

  /* ======================================================
     PAYMENT SUCCESS SCREEN
  ====================================================== */

  if (paymentSuccess) {
    return (
      <div className="vsp-page">
        <main className="vsp-container">
          <div className="vsp-success-card">
            <div className="vsp-success-icon">
              <CheckCircle2
                size={44}
              />
            </div>

            <span className="vsp-success-label">
              PAYMENT SUCCESSFUL
            </span>

            <h1>
              Subscription Activated
            </h1>

            <p>
              Your VAKRA subscription payment has been verified successfully.
            </p>

            <div className="vsp-success-details">
              <div>
                <span>
                  Business
                </span>

                <strong>
                  {
                    paymentSuccess.businessName ||
                    'VAKRA Vendor'
                  }
                </strong>
              </div>

              <div>
                <span>
                  Plan
                </span>

                <strong>
                  {
                    paymentSuccess.planName
                  }
                </strong>
              </div>

              <div>
                <span>
                  Amount Paid
                </span>

                <strong>
                  ₹
                  {Number(
                    paymentSuccess.amount ||
                      0
                  ).toLocaleString(
                    'en-IN'
                  )}
                </strong>
              </div>

              <div>
                <span>
                  Receipt No.
                </span>

                <strong>
                  {
                    paymentSuccess.receiptNo ||
                    'Generated'
                  }
                </strong>
              </div>
            </div>

            <div className="vsp-secure-footer">
              <ShieldCheck
                size={17}
              />

              Payment and subscription securely verified by VAKRA.
            </div>

            {paymentSuccess.paymentId && (
              <button
                type="button"
                className="vsp-retry-button"
                disabled={
                  downloadingInvoiceId ===
                  Number(
                    paymentSuccess.paymentId
                  )
                }
                onClick={() =>
                  handleDownloadInvoice(
                    paymentSuccess.paymentId,
                    paymentSuccess.receiptNo
                  )
                }
              >
                {downloadingInvoiceId ===
                Number(
                  paymentSuccess.paymentId
                ) ? (
                  <>
                    <Loader2
                      size={17}
                      className="vsp-spinner"
                    />

                    Downloading Invoice...
                  </>
                ) : (
                  <>
                    <Download
                      size={17}
                    />

                    Download Invoice
                  </>
                )}
              </button>
            )}

            <button
              type="button"
              className="vsp-retry-button"
              onClick={() => {
                setPaymentSuccess(
                  null
                );

                loadSubscription();
              }}
            >
              Back to Subscription & Billing
            </button>
          </div>
        </main>
      </div>
    );
  }

  /* ======================================================
     MAIN PAGE
  ====================================================== */

  const agreementAccepted =
    Boolean(
      agreement?.accepted
    ) ||
    agreement?.status ===
      'accepted' ||
    agreement?.status ===
      'active';

  return (
    <div className="vsp-page">
      {/* =================================================
          HEADER
      ================================================= */}

      <header className="vsp-header">
        <div className="vsp-brand">
          <div className="vsp-logo">
            V
          </div>

          <div>
            <strong>
              VAKRA
            </strong>

            <span>
              BUSINESS PLATFORM
            </span>
          </div>
        </div>

        <div className="vsp-secure-chip">
          <LockKeyhole
            size={15}
          />

          Secure Billing
        </div>
      </header>

      <main className="vsp-container">
        {/* =================================================
            INTRO
        ================================================= */}

        <section className="vsp-intro">
          <span>
            SUBSCRIPTION & BILLING
          </span>

          <h1>
            Choose Your VAKRA Plan
          </h1>

          <p>
            Select the subscription plan that best fits your business, accept the service agreement and pay securely online.
          </p>
        </section>

        {/* =================================================
            MESSAGES
        ================================================= */}

        {error && (
          <div className="vsp-error">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="vsp-success-message">
            <CheckCircle2
              size={18}
            />

            {
              successMessage
            }
          </div>
        )}

        {/* =================================================
            AVAILABLE PLANS
        ================================================= */}

        <section className="vsp-plans-section">
          <div className="vsp-plans-heading">
            <div>
              <span>
                AVAILABLE PLANS
              </span>

              <h2>
                Select Your Subscription
              </h2>

              <p>
                You can choose the duration and plan that suits your business.
              </p>
            </div>
          </div>

          {plansLoading ? (
            <div className="vsp-plans-loading">
              <Loader2
                size={23}
                className="vsp-spinner"
              />

              Loading subscription plans...
            </div>
          ) : plans.length ===
            0 ? (
            <div className="vsp-history-empty">
              No subscription plans are currently available.
            </div>
          ) : (
            <div className="vsp-plan-grid">
              {plans.map(
                (plan) => {
                  const isSelected =
                    subscription?.planId ===
                      plan.planId &&
                    subscription?.paymentStatus !==
                      'paid';

                  const isCurrentPaid =
                    subscription?.planId ===
                      plan.planId &&
                    subscription?.paymentStatus ===
                      'paid';

                  return (
                    <article
                      key={
                        plan.planId
                      }
                      className={`vsp-plan-card ${
                        isSelected
                          ? 'selected'
                          : ''
                      } ${
                        isCurrentPaid
                          ? 'paid-plan'
                          : ''
                      }`}
                    >
                      <div className="vsp-plan-card-top">
                        <span className="vsp-plan-duration">
                          <CalendarDays
                            size={14}
                          />

                          {
                            plan.durationDays
                          }{' '}
                          Days
                        </span>

                        {isSelected && (
                          <span className="vsp-selected-chip">
                            Selected
                          </span>
                        )}

                        {isCurrentPaid && (
                          <span className="vsp-active-chip">
                            Current
                          </span>
                        )}
                      </div>

                      <h3>
                        {
                          plan.planName
                        }
                      </h3>

                      <div className="vsp-plan-price-big">
                        <span>
                          ₹
                        </span>

                        <strong>
                          {Number(
                            plan.price ||
                              0
                          ).toLocaleString(
                            'en-IN'
                          )}
                        </strong>
                      </div>

                      <p>
                        Valid for{' '}
                        {
                          plan.durationDays
                        }{' '}
                        days
                      </p>

                      <button
                        type="button"
                        className="vsp-select-plan-button"
                        disabled={
                          selectingPlanId !==
                            null
                        }
                        onClick={() =>
                          handleSelectPlan(
                            plan
                          )
                        }
                      >
                        {selectingPlanId ===
                        plan.planId ? (
                          <>
                            <Loader2
                              size={16}
                              className="vsp-spinner"
                            />

                            Selecting...
                          </>
                        ) : isSelected ? (
                          'Selected - Click Again'
                        ) : isCurrentPaid ? (
                          'Choose This Plan'
                        ) : (
                          'Select Plan'
                        )}
                      </button>
                    </article>
                  );
                }
              )}
            </div>
          )}
        </section>

        {/* =================================================
            NO PLAN SELECTED
        ================================================= */}

        {!subscription && (
          <section className="vsp-no-selection">
            <CreditCard
              size={40}
            />

            <h2>
              Select a Subscription Plan
            </h2>

            <p>
              Choose one of the available plans above to continue with the agreement and payment.
            </p>
          </section>
        )}

        {/* =================================================
            SELECTED / CURRENT SUBSCRIPTION
        ================================================= */}

        {subscription && (
          <>
            <div className="vsp-grid">
              {/* =============================================
                  BUSINESS
              ============================================= */}

              <section className="vsp-business-card">
                <div className="vsp-section-title">
                  <Store
                    size={19}
                  />

                  <div>
                    <strong>
                      Business Details
                    </strong>

                    <span>
                      Subscription Holder
                    </span>
                  </div>
                </div>

                <div className="vsp-business-name">
                  <div className="vsp-business-avatar">
                    {String(
                      subscription.businessName ||
                        'V'
                    )
                      .charAt(0)
                      .toUpperCase()}
                  </div>

                  <div>
                    <strong>
                      {
                        subscription.businessName
                      }
                    </strong>

                    <span>
                      {
                        subscription.ownerName ||
                        ''
                      }
                    </span>
                  </div>
                </div>

                <div className="vsp-info-list">
                  {subscription.mobile && (
                    <div>
                      <span>
                        Mobile
                      </span>

                      <strong>
                        {
                          subscription.mobile
                        }
                      </strong>
                    </div>
                  )}

                  {subscription.email && (
                    <div>
                      <span>
                        Email
                      </span>

                      <strong>
                        {
                          subscription.email
                        }
                      </strong>
                    </div>
                  )}

                  <div>
                    <span>
                      Business Type
                    </span>

                    <strong>
                      {
                        subscription.businessType ||
                        'VAKRA Vendor'
                      }
                    </strong>
                  </div>
                </div>
              </section>

              {/* =============================================
                  BILLING
              ============================================= */}

              <section className="vsp-payment-card">
                <div className="vsp-plan-badge">
                  <CreditCard
                    size={16}
                  />

                  {subscription.paymentStatus ===
                  'paid'
                    ? 'CURRENT SUBSCRIPTION'
                    : 'SELECTED SUBSCRIPTION'}
                </div>

                <h2>
                  {
                    subscription.planName
                  }{' '}
                  Plan
                </h2>

                <div className="vsp-price">
                  <IndianRupee
                    size={25}
                  />

                  <strong>
                    {Number(
                      subscription.amount ||
                        0
                    ).toLocaleString(
                      'en-IN'
                    )}
                  </strong>
                </div>

                <span className="vsp-price-note">
                  Subscription Fee
                </span>

                <div className="vsp-plan-details">
                  <div>
                    <span>
                      Start Date
                    </span>

                    <strong>
                      {formatDisplayDate(
                        subscription.startDate
                      )}
                    </strong>
                  </div>

                  <div>
                    <span>
                      Expiry Date
                    </span>

                    <strong>
                      {formatDisplayDate(
                        subscription.expiryDate
                      )}
                    </strong>
                  </div>

                  <div>
                    <span>
                      Subscription Status
                    </span>

                    <strong
                      className={
                        subscription.status ===
                          'active'
                          ? 'vsp-paid'
                          : 'vsp-pending'
                      }
                    >
                      {
                        subscription.status ||
                        'Pending'
                      }
                    </strong>
                  </div>

                  <div>
                    <span>
                      Payment Status
                    </span>

                    <strong
                      className={
                        subscription.paymentStatus ===
                          'paid'
                          ? 'vsp-paid'
                          : 'vsp-pending'
                      }
                    >
                      {
                        subscription.paymentStatus ||
                        'Pending'
                      }
                    </strong>
                  </div>
                </div>

                {/* ===========================================
                    AGREEMENT
                =========================================== */}

                <div className="vsp-agreement-box">
                  <div className="vsp-agreement-box-icon">
                    <FileText
                      size={21}
                    />
                  </div>

                  <div className="vsp-agreement-box-content">
                    <strong>
                      VAKRA Service & Subscription Agreement
                    </strong>

                    <span>
                      Version{' '}
                      {
                        agreement
                          ?.agreementVersion ||
                        '1.0'
                      }
                    </span>

                    <div
                      className={`vsp-agreement-status ${
                        agreementAccepted
                          ? 'accepted'
                          : 'pending'
                      }`}
                    >
                      {agreementAccepted
                        ? 'Agreement Accepted'
                        : 'Agreement Acceptance Required'}
                    </div>
                  </div>

                  <button
                    type="button"
                    className="vsp-view-agreement-button"
                    onClick={
                      handleOpenAgreement
                    }
                  >
                    View Agreement
                  </button>
                </div>

                {/* ===========================================
                    PAYMENT BUTTON
                =========================================== */}

                {subscription.paymentStatus !==
                'paid' ? (
                  <>
                    {!agreementAccepted && (
                      <div className="vsp-payment-lock-message">
                        <LockKeyhole
                          size={16}
                        />

                        Review and accept the agreement to enable payment.
                      </div>
                    )}

                    <button
                      type="button"
                      className="vsp-pay-button"
                      onClick={
                        handlePayNow
                      }
                      disabled={
                        paying ||
                        !agreementAccepted
                      }
                    >
                      {paying ? (
                        <>
                          <Loader2
                            size={19}
                            className="vsp-spinner"
                          />

                          Processing...
                        </>
                      ) : (
                        <>
                          <LockKeyhole
                            size={18}
                          />

                          Pay ₹
                          {Number(
                            subscription.amount ||
                              0
                          ).toLocaleString(
                            'en-IN'
                          )}{' '}
                          Now
                        </>
                      )}
                    </button>
                  </>
                ) : (
                  <div className="vsp-paid-box">
                    <CheckCircle2
                      size={19}
                    />

                    Payment Completed
                  </div>
                )}

                <div className="vsp-payment-methods">
                  <span>
                    Pay Securely Using
                  </span>

                  <div>
                    UPI
                  </div>

                  <div>
                    Cards
                  </div>

                  <div>
                    Net Banking
                  </div>
                </div>

                <div className="vsp-protection">
                  <ShieldCheck
                    size={18}
                  />

                  <p>
                    Your payment is securely verified by the VAKRA server. Razorpay secret credentials are never exposed in your browser.
                  </p>
                </div>
              </section>
            </div>

            {/* ===============================================
                PAYMENT HISTORY
            =============================================== */}

            <section className="vsp-history-panel">
              <div className="vsp-history-heading">
                <strong>
                  Payment History
                </strong>

                <span>
                  Payment records for the selected subscription.
                </span>
              </div>

              {payments.length ===
              0 ? (
                <div className="vsp-history-empty">
                  No payment history available.
                </div>
              ) : (
                <div className="vsp-history-table-wrap">
                  <table className="vsp-history-table">
                    <thead>
                      <tr>
                        <th>
                          Receipt
                        </th>

                        <th>
                          Amount
                        </th>

                        <th>
                          Mode
                        </th>

                        <th>
                          Date
                        </th>

                        <th>
                          Transaction
                        </th>

                        <th>
                          Status
                        </th>

                        <th>
                          Invoice
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {payments.map(
                        (
                          payment
                        ) => (
                          <tr
                            key={
                              payment.paymentId
                            }
                          >
                            <td>
                              {
                                payment.receiptNo ||
                                '—'
                              }
                            </td>

                            <td className="vsp-history-amount">
                              ₹
                              {Number(
                                payment.amount ||
                                  0
                              ).toLocaleString(
                                'en-IN'
                              )}
                            </td>

                            <td>
                              {
                                payment.paymentMode ||
                                '—'
                              }
                            </td>

                            <td>
                              {
                                payment.paymentDate ||
                                '—'
                              }
                            </td>

                            <td>
                              {
                                payment.transactionId ||
                                '—'
                              }
                            </td>

                            <td>
                              <span
                                className={`vsp-history-status ${
                                  payment.status ||
                                  ''
                                }`}
                              >
                                {
                                  payment.status ||
                                  '—'
                                }
                              </span>
                            </td>

                            <td>
                              {payment.status ===
                              'paid' ? (
                                <button
                                  type="button"
                                  className="vsp-view-agreement-button"
                                  disabled={
                                    downloadingInvoiceId ===
                                    Number(
                                      payment.paymentId
                                    )
                                  }
                                  onClick={() =>
                                    handleDownloadInvoice(
                                      payment.paymentId,
                                      payment.receiptNo
                                    )
                                  }
                                >
                                  {downloadingInvoiceId ===
                                  Number(
                                    payment.paymentId
                                  ) ? (
                                    <>
                                      <Loader2
                                        size={14}
                                        className="vsp-spinner"
                                      />

                                      Downloading...
                                    </>
                                  ) : (
                                    <>
                                      <Download
                                        size={14}
                                      />

                                      Download
                                    </>
                                  )}
                                </button>
                              ) : (
                                '—'
                              )}
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
      </main>

      {/* =================================================
          AGREEMENT MODAL
      ================================================= */}

      {agreementOpen &&
        agreement && (
          <div className="vsp-agreement-backdrop">
            <div className="vsp-agreement-modal">
              <div className="vsp-agreement-modal-header">
                <div>
                  <span>
                    VAKRA
                  </span>

                  <h2>
                    Service & Subscription Agreement
                  </h2>
                </div>

                <button
                  type="button"
                  onClick={() =>
                    setAgreementOpen(
                      false
                    )
                  }
                >
                  <X
                    size={21}
                  />
                </button>
              </div>

              <div className="vsp-agreement-summary">
                <div>
                  <span>
                    Agreement No.
                  </span>

                  <strong>
                    {
                      agreement.agreementNo ||
                      'Pending'
                    }
                  </strong>
                </div>

                <div>
                  <span>
                    Version
                  </span>

                  <strong>
                    {
                      agreement.agreementVersion ||
                      '1.0'
                    }
                  </strong>
                </div>

                <div>
                  <span>
                    Business
                  </span>

                  <strong>
                    {
                      subscription?.businessName ||
                      ''
                    }
                  </strong>
                </div>

                <div>
                  <span>
                    Plan
                  </span>

                  <strong>
                    {
                      subscription?.planName ||
                      ''
                    }
                  </strong>
                </div>

                <div>
                  <span>
                    Amount
                  </span>

                  <strong>
                    ₹
                    {Number(
                      subscription?.amount ||
                        0
                    ).toLocaleString(
                      'en-IN'
                    )}
                  </strong>
                </div>

                <div>
                  <span>
                    Validity
                  </span>

                  <strong>
                    {formatDisplayDate(
                      subscription?.startDate
                    )}
                    {' → '}
                    {formatDisplayDate(
                      subscription?.expiryDate
                    )}
                  </strong>
                </div>
              </div>

              <div className="vsp-agreement-terms">
                {String(
                  agreement.terms || ''
                )
                  .split('\n')
                  .map((line, index) => {
                    const text =
                      String(line || '').trim();

                    /*
                      EMPTY LINE
                    */
                    if (!text) {
                      return (
                        <div
                          key={index}
                          className="vsp-agreement-space"
                        />
                      );
                    }

                    /*
                      MAIN AGREEMENT TITLE
                    */
                    if (
                      text ===
                      'VAKRA SERVICE & SUBSCRIPTION AGREEMENT'
                    ) {
                      return (
                        <h2
                          key={index}
                          className="vsp-agreement-document-title"
                        >
                          {text}
                        </h2>
                      );
                    }

                    /*
                      NUMBERED SECTION HEADINGS
                      Example:
                      1. AGREEMENT
                      2. VENDOR DETAILS
                    */
                    if (
                      /^\d+\.\s+/.test(text)
                    ) {
                      return (
                        <h3
                          key={index}
                          className="vsp-agreement-section-heading"
                        >
                          {text}
                        </h3>
                      );
                    }

                    /*
                      BULLET POINTS
                    */
                    if (
                      text.startsWith('- ')
                    ) {
                      return (
                        <div
                          key={index}
                          className="vsp-agreement-bullet"
                        >
                          <span>
                            •
                          </span>

                          <p>
                            {text.substring(2)}
                          </p>
                        </div>
                      );
                    }

                    /*
                      SUB POINTS:
                      (a), (b), etc.
                    */
                    if (
                      /^\([a-z]\)/i.test(text)
                    ) {
                      return (
                        <p
                          key={index}
                          className="vsp-agreement-subpoint"
                        >
                          {text}
                        </p>
                      );
                    }

                    /*
                      NORMAL PARAGRAPH
                    */
                    return (
                      <p
                        key={index}
                        className="vsp-agreement-paragraph"
                      >
                        {text}
                      </p>
                    );
                  })}
              </div>

              {!agreementAccepted ? (
                <div className="vsp-agreement-accept-area">
                  <label className="vsp-agreement-checkbox">
                    <input
                      type="checkbox"
                      checked={
                        agreementChecked
                      }
                      onChange={(
                        event
                      ) =>
                        setAgreementChecked(
                          event.target.checked
                        )
                      }
                    />

                    <span>
                      I have read, understood and agree to the VAKRA Service & Subscription Agreement.
                    </span>
                  </label>

                  <button
                    type="button"
                    className="vsp-accept-agreement"
                    disabled={
                      !agreementChecked ||
                      acceptingAgreement
                    }
                    onClick={
                      handleAcceptAgreement
                    }
                  >
                    {acceptingAgreement ? (
                      <>
                        <Loader2
                          size={17}
                          className="vsp-spinner"
                        />

                        Accepting...
                      </>
                    ) : (
                      <>
                        <CheckCircle2
                          size={17}
                        />

                        Accept Agreement
                      </>
                    )}
                  </button>
                </div>
              ) : (
                <div className="vsp-agreement-accepted-message">
                  <CheckCircle2
                    size={19}
                  />

                  <div>
                    <strong>
                      Agreement Accepted
                    </strong>

                    <span>
                      Accepted on{' '}
                      {
                        agreement.acceptedAt ||
                        'recorded date'
                      }
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

      <footer className="vsp-footer">
        © {new Date().getFullYear()}{' '}
        VAKRA · Secure Subscription Billing
      </footer>
    </div>
  );
};

export default SubscriptionPayment;
